import { useSnackbar } from 'notistack';
import { useCallback, useRef, useState } from 'react';
import { CourseDetailEntity, CoursePayload } from 'src/entities/Course.entity';
import { releaseCourseSave, tryAcquireCourseSave } from 'src/hooks/courseSaveGate';
import { CourseMasterError, parseCourseMasterError } from 'src/utils/courseMasterError';

import { courseLabel } from './courseMasterUtils';

type Mismatch = Extract<CourseMasterError, { code: 'trip_group_mismatch' }>;
export type CourseSendFn = (data: CoursePayload) => Promise<CourseDetailEntity>;

/**
 * コースの作成・更新で API のエラー code ごとに画面の分岐をそろえる（新規ダイアログと詳細の両方で使う）。
 * - trip_group_mismatch → mismatch（ダイアログで align を選び resolveMismatch で送り直す。設計書 C3 ルール 2）
 * - truck_shared_by_trip_groups / first_trip_not_in_timetables → alert（保存を止める。利用者が直す）
 * - それ以外 → スナックバー
 */
export function useCourseSaveFlow(
  send: CourseSendFn,
  onSaved: (course: CourseDetailEntity) => void,
  courseId: number | null = null,
) {
  const { enqueueSnackbar } = useSnackbar();
  const [pending, setPending] = useState(false);
  const [mismatch, setMismatch] = useState<{ data: CoursePayload; error: Mismatch } | null>(null);
  const [alert, setAlert] = useState<string | null>(null);
  // 同じ tick の二重発火（blur と change など）で送信が重ならないよう、state でなく同期的に見る（判断事項 16）。
  // 既存コース（courseId あり）はルート・適用終了と共通のゲート、新規作成（null）はこの hook の中だけで止める
  const localInFlight = useRef(false);
  const acquire = useCallback(() => {
    if (courseId == null) {
      if (localInFlight.current) return false;
      localInFlight.current = true;
      return true;
    }
    return tryAcquireCourseSave(courseId);
  }, [courseId]);
  const release = useCallback(() => {
    if (courseId == null) localInFlight.current = false;
    else releaseCourseSave(courseId);
  }, [courseId]);

  const run = useCallback(
    async (data: CoursePayload): Promise<boolean> => {
      if (!acquire()) {
        enqueueSnackbar('保存中です。終わってからもう一度変更してください', { variant: 'warning' });
        return false;
      }
      setPending(true);
      setAlert(null);
      try {
        onSaved(await send(data));
        return true;
      } catch (e) {
        const err = parseCourseMasterError(e);
        switch (err.code) {
          case 'trip_group_mismatch':
            setMismatch({ data, error: err });
            break;
          case 'first_trip_not_in_timetables':
            setAlert(`${err.message}（${err.timetables.map((t) => t.name).join('、')}）`);
            break;
          case 'truck_shared_by_trip_groups':
            setAlert(`${err.message}（${err.courses.map(courseLabel).join('、')}）`);
            break;
          default:
            enqueueSnackbar(err.message, { variant: 'error' });
        }
        return false;
      } finally {
        release();
        setPending(false);
      }
    },
    [send, onSaved, enqueueSnackbar, acquire, release],
  );

  // Why not setState の updater の中で送り直す: updater は純粋関数で、再評価されると送信が重なる
  const resolveMismatch = useCallback(
    (align: 'self' | 'target') => {
      if (!mismatch) return;
      const { data } = mismatch;
      setMismatch(null);
      void run({ ...data, align });
    },
    [mismatch, run],
  );

  return {
    run,
    pending,
    mismatch,
    resolveMismatch,
    dismissMismatch: () => setMismatch(null),
    alert,
    clearAlert: () => setAlert(null),
  };
}
